import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import useSetAuthOnScreenFocus from '@/hooks/auth/useSetAuthOnScreenFocus';
import {
  useAddPostBoardCommentOfBoardPostMutation,
  useGetCommunityUserQuery,
  useSetLikeBoardCommentMutation,
  useSetLikeBoardPostMutation,
} from '@/hooks/community/mobile/community.gql.impl';
import useGetBoardPostByPostDetail from '@/hooks/community/mobile/useGetBoardPostByPostDetail';
import useAuthModal from '@/hooks/useAuthModal';
import useCustomRouter from '@/hooks/useCustomRouter';
import { tokenState } from '@/recoil/atoms/common/auth';
import { communityUserService } from '@/services/community';
import { BoardPostDetailService } from '@/services/community/BoardPostDetailService';
import { GetBoardPostQuery } from '@/types/bff/gql-schema';
import { BoardPostDetail, CommunityUser } from '@/types/community';
import { mobileLogger } from '@/utils/logger';
import { isWebView } from '@/utils/mobile/RNPlugins';

const COMMENT_MAX_LENGTH = 1000;

interface Props {
  boardPostPid: string;
}

interface BoardPostDetailViewModel {
  comment: string;
  addCommentLoading: boolean;
  isShareModalOpen: boolean;
  boardPostDetail?: BoardPostDetail;
  isBoardPostDetailLoading: boolean;
  boardPostRefresh: () => Promise<void>;
  userProfile?: CommunityUser;
  isLoggedIn: boolean;
  updatePostLikeStatus: () => void;
  updateCommentLikeStatus: (boardCommentPid: string) => void;
  onSubmitCommentByPost: (content: string) => Promise<boolean | void>;
  onChangeCommentInput: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onToggleShareModal: (on: boolean) => void;
  onClearComment: () => void;
  onOpenAuthModal: () => void;
  pageName: string;
}

const useBoardPostDetailViewModel = ({ boardPostPid }: Props): BoardPostDetailViewModel => {
  useSetAuthOnScreenFocus();
  const queryClient = useQueryClient();
  const token = useRecoilValue(tokenState);
  const { setShowAuthModal } = useAuthModal();
  const { pageName } = useCustomRouter();
  const [comment, setComment] = useState('');
  const [addCommentLoading, setAddCommentLoading] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);

  const isLoggedIn = !!token;

  // 상세페이지 가져오기
  const {
    data,
    isLoading: isBoardPostDetailLoading,
    refetch: refetchBoardPostDetail,
  } = useGetBoardPostByPostDetail(boardPostPid);

  const boardPostDetail = BoardPostDetailService.boardPostDetailParser(data);

  const boardPostRefresh = async () => {
    await refetchBoardPostDetail();
  };

  // TODO: 추후 마이페이지 관련 viewModel 추가되면 변경할예정
  // 유저 정보 가져오기
  const { data: userProfile } = useGetCommunityUserQuery(
    {},
    {
      enabled: !!isLoggedIn,
      select: (data) => communityUserService(data),
    },
  );

  // 글 좋아요 설정
  const { mutate: setLikeBoardPost } = useSetLikeBoardPostMutation({
    onMutate: async (data) => {
      await queryClient.cancelQueries(['setLikeBoardPost']);

      const boardPostPid = data.boardPostPid;
      await queryClient.cancelQueries(['getBoardPost', { boardPostPid }]);

      const boardDetail: GetBoardPostQuery | undefined = queryClient.getQueryData(['getBoardPost', { boardPostPid }]);

      if (boardDetail) {
        queryClient.setQueryData(['getBoardPost', { boardPostPid }], () => {
          return {
            ...boardDetail,
            boardPost: {
              ...boardDetail.boardPost,
              isLikedByMe: true,
              likeCount: boardDetail.boardPost.likeCount + 1,
            },
          };
        });
      }

      return {
        boardDetail,
      };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(['getBoardPost', { boardPostPid }], context?.boardDetail);
    },
  });

  const updatePostLikeStatus = () => {
    if (!isLoggedIn) {
      onOpenAuthModal();
      return;
    }
    return setLikeBoardPost(
      { boardPostPid },
      {
        onSettled: () => onInvalidateQueryByOnSuccess(boardPostPid),
      },
    );
  };

  // 댓글 좋아요 설정
  // todo: 댓글 api 변경 후 낙관적 업데이트 추가
  const { mutate: setLikeBoardComment } = useSetLikeBoardCommentMutation();

  const updateCommentLikeStatus = (boardCommentPid: string) => {
    if (!isLoggedIn) {
      onOpenAuthModal();
      return;
    }
    return setLikeBoardComment(
      { boardCommentPid },
      {
        onSuccess: () => onInvalidateQueryByOnSuccess(boardPostPid),
      },
    );
  };

  // 댓글 등록
  const { mutateAsync: addCommentOfPost } = useAddPostBoardCommentOfBoardPostMutation({ retry: 0 });

  const onSubmitCommentByPost = async (content: string) => {
    if (!isLoggedIn) {
      onOpenAuthModal();
      return;
    }
    setAddCommentLoading(true);
    const result = await addCommentOfPost(
      { request: { boardPostPid, content } },
      {
        onSuccess: () => {
          mobileLogger({
            logger: {
              eventName: 'submit_comment',
              pageName,
            },
          });
          onClearComment();
          onInvalidateQueryByOnSuccess(boardPostPid);
        },
        onSettled: () => {
          setAddCommentLoading(false);
        },
      },
    );
    return result.postBoardCommentOfBoardPost;
  };

  const onInvalidateQueryByOnSuccess = (boardPostPid: string) => {
    queryClient.invalidateQueries(['getBoardPost', { boardPostPid }]);
  };

  // onChange 댓글
  const onChangeCommentInput = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (!isWebView() && !isLoggedIn) {
      return;
    }
    setComment((prev) => {
      const value = e.target.value;
      if (value.length > COMMENT_MAX_LENGTH) {
        return prev;
      }

      return value.replace(/^\s+/g, '');
    });
  };

  // 댓글 input clear
  const onClearComment = () => {
    setComment('');
  };

  // toggle 공유하기 모달
  const onToggleShareModal = (on: boolean) => {
    mobileLogger({
      logger: {
        eventName: 'post_share_click',
        pageName,
        payload: {
          id: boardPostPid,
        },
      },
    });
    setIsShareModalOpen(on);
  };

  const onOpenAuthModal = () => {
    setShowAuthModal(true);
  };

  useEffect(() => {
    if (isLoggedIn) refetchBoardPostDetail();
  }, [isLoggedIn, refetchBoardPostDetail]);

  return {
    comment,
    addCommentLoading,
    isShareModalOpen,
    boardPostDetail,
    isBoardPostDetailLoading,
    boardPostRefresh,
    userProfile,
    isLoggedIn,
    updatePostLikeStatus,
    updateCommentLikeStatus,
    onSubmitCommentByPost,
    onChangeCommentInput,
    onToggleShareModal,
    onClearComment,
    onOpenAuthModal,
    pageName,
  };
};

export default useBoardPostDetailViewModel;
